import { useEffect } from 'react';
import { getCategoryLabel } from '@3dera/core/utils/categoryTranslations';
import { getEffectivePrice } from '@3dera/core/utils/pricing';

/** Site base URL (tarayıcıda origin, aksi halde varsayılan) */
export const getBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return import.meta.env.VITE_SITE_URL || 'https://3dera.io';
};

/** Relative veya mutlak görsel URL'ini absolute yapar */
export const toAbsoluteImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return undefined;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
  const base = getBaseUrl();
  return imageUrl.startsWith('/') ? `${base}${imageUrl}` : `${base}/${imageUrl}`;
};

/**
 * SEO Hook - Dinamik meta tag yönetimi
 * @param {Object} seoData - SEO verileri
 * @param {string} seoData.title - Sayfa başlığı
 * @param {string} seoData.description - Sayfa açıklaması
 * @param {string} seoData.keywords - Anahtar kelimeler
 * @param {string} seoData.image - Open Graph görseli
 * @param {string} seoData.url - Sayfa URL'i
 * @param {string} seoData.type - Open Graph tipi (website, article, product)
 * @param {Object} seoData.structuredData - JSON-LD structured data
 */
export const useSEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  structuredData
} = {}) => {
  useEffect(() => {
    // Title güncelleme
    if (title) {
      const fullTitle = title.includes('3DEra') ? title : `${title} | 3DEra`;
      document.title = fullTitle;
      
      // Open Graph title
      updateMetaTag('property', 'og:title', fullTitle);
      updateMetaTag('name', 'twitter:title', fullTitle);
    }

    // Description güncelleme
    if (description) {
      updateMetaTag('name', 'description', description);
      updateMetaTag('property', 'og:description', description);
      updateMetaTag('name', 'twitter:description', description);
    }

    // Keywords güncelleme
    if (keywords) {
      updateMetaTag('name', 'keywords', keywords);
    }

    // Image güncelleme
    if (image) {
      updateMetaTag('property', 'og:image', image);
      updateMetaTag('name', 'twitter:image', image);
    }

    // URL güncelleme
    if (url) {
      updateMetaTag('property', 'og:url', url);
      updateCanonicalUrl(url);
    }

    // Open Graph type
    updateMetaTag('property', 'og:type', type);

    // Structured Data ekleme
    if (structuredData) {
      addStructuredData(structuredData);
    }

  }, [title, description, keywords, image, url, type, structuredData]);
};

/**
 * Meta tag güncelleme yardımcı fonksiyonu
 */
const updateMetaTag = (attribute, selector, content) => {
  const metaTag = document.querySelector(`meta[${attribute}="${selector}"]`);
  if (metaTag) {
    metaTag.setAttribute('content', content);
  } else {
    // Meta tag yoksa oluştur
    const newMetaTag = document.createElement('meta');
    newMetaTag.setAttribute(attribute, selector);
    newMetaTag.setAttribute('content', content);
    document.head.appendChild(newMetaTag);
  }
};

/**
 * Canonical URL güncelleme
 */
const updateCanonicalUrl = (url) => {
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (canonicalLink) {
    canonicalLink.setAttribute('href', url);
  } else {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', url);
    document.head.appendChild(canonicalLink);
  }
};

/**
 * Structured Data (JSON-LD) ekleme
 * Sadece hook tarafından eklenen script'leri kaldırır; index.html'deki Organization korunur.
 */
const addStructuredData = (data) => {
  const dynamicScripts = document.querySelectorAll('script[type="application/ld+json"][data-seo-dynamic="true"]');
  dynamicScripts.forEach((el) => el.remove());

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-dynamic', 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

/**
 * SEO için hazır meta tag'ler
 */
const base = () => getBaseUrl();

export const SEO_META = {
  HOME: {
    title: '3DEra - 3D Model Marketplace',
    description: 'Türkiye\'nin 3D model marketplace\'i. Binlerce 3D model, özel tasarım hizmetleri ve 3D baskı çözümleri.',
    keywords: '3d model, 3d baskı, 3d yazıcı, stl dosya, 3d tasarım, marketplace, türkiye',
    url: `${base()}/`
  },
  MARKETPLACE: {
    title: '3D Model Galerisi',
    description: 'Binlerce hazır 3D model ve özel çizim dosyası. Figür, dekorasyon, oyuncak, takı ve daha fazlası!',
    keywords: '3d model galeri, hazır 3d model, stl dosya indir, 3d çizim, figür modeli, dekorasyon',
    url: `${base()}/marketplace`
  },
  SELLERS: {
    title: '3D Baskı Satıcıları',
    description: 'Güvenilir 3D baskı hizmeti veren satıcılarımız. Yakınındaki en iyi 3D yazıcı sahipleri ile tanış!',
    keywords: '3d baskı hizmeti, 3d yazıcı sahibi, 3d baskı satıcı, yakınımda 3d baskı',
    url: `${base()}/sellers`
  },
  ABOUT: {
    title: 'Hakkımızda - 3DEra',
    description: '3D baskı teknolojisini demokratikleştiren platform. 3DEra\'nın hikayesi ve vizyonu hakkında bilgi alın.',
    keywords: '3dera hakkında, 3d baskı platformu, 3d teknoloji, hikayemiz',
    url: `${base()}/about`
  },
  CONTACT: {
    title: 'İletişim - 3DEra',
    description: '3DEra ile iletişime geçin. Sorularınız, önerileriniz ve destek talepleriniz için bizimle iletişime geçin.',
    keywords: '3dera iletişim, destek, yardım, soru, öneri',
    url: `${base()}/contact`
  },
  BECOME_SELLER: {
    title: 'Satıcı Ol - 3DEra',
    description: '3DEra\'da satıcı olun. 3D modellerinizi veya özel çizim hizmetinizi sunun, gelir elde edin.',
    keywords: '3dera satıcı ol, 3d baskı satıcılık, 3d model satış, satıcı başvurusu',
    url: `${base()}/become-seller`
  },
  PHOTO_TO_3D: {
    title: 'Fotoğraftan 3D Model - 3DEra',
    description: 'Fotoğrafınızdan 3D model oluşturun. Yapay zeka destekli fotoğraftan 3D dönüşüm hizmeti.',
    keywords: 'fotoğraftan 3d, 3d model oluşturma, foto 3d, ai 3d model',
    url: `${base()}/photo-to-3d`
  }
};

/**
 * Ürün sayfası için SEO meta'ları
 * @param {Object} product - Ürün objesi
 * @param {string} productId - Ürün ID (URL için)
 */
export const getProductSEO = (product, productId) => {
  const desc = product.description && String(product.description).trim();
  const shortDesc = desc ? (desc.length > 150 ? `${desc.substring(0, 150).trim()}…` : desc) : '';
  const metaDesc = shortDesc
    ? `${product.name} - ${shortDesc} 3DEra'da ${getCategoryLabel(product.category)} kategorisinde.`
    : `${product.name} - 3DEra'da ${getCategoryLabel(product.category)} kategorisinde.`;
  const base = getBaseUrl();
  const url = productId ? `${base}/product/${productId}` : undefined;
  const image = toAbsoluteImageUrl(product.images?.[0]);
  const imagesAbsolute = Array.isArray(product.images) ? product.images.map(toAbsoluteImageUrl).filter(Boolean) : undefined;
  const eff = getEffectivePrice(product);
  return {
    title: `${product.name} - 3D Model`,
    description: metaDesc.substring(0, 160),
    keywords: [product.name, getCategoryLabel(product.category), '3d model', (product.tags || []).join(', ')].filter(Boolean).join(', '),
    image,
    url,
    type: 'product',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": product.description,
      "image": imagesAbsolute || image,
      "url": url,
      "category": getCategoryLabel(product.category),
      "offers": {
        "@type": "Offer",
        "price": eff.price,
        "priceCurrency": "TRY",
        ...(eff.isDiscounted && eff.endAt ? { "priceValidUntil": new Date(eff.endAt).toISOString() } : {}),
        "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
      },
      "brand": {
        "@type": "Brand",
        "name": "3DEra"
      }
    }
  };
};

/**
 * Satıcı sayfası için SEO meta'ları
 * @param {Object} seller - Satıcı objesi
 * @param {string} sellerId - Satıcı ID (URL için)
 */
export const getSellerSEO = (seller, sellerId) => {
  const base = getBaseUrl();
  const url = sellerId ? `${base}/seller/${sellerId}` : undefined;
  const image = toAbsoluteImageUrl(seller.profileImageUrl);
  const loc = seller.location;
  const city = loc?.city || '';
  const country = loc?.country || '';
  const locationStr = [city, country].filter(Boolean).join(', ');
  return {
    title: `${seller.publicName || seller.name || 'Satıcı'} - 3D Baskı Satıcısı`,
    description: locationStr
      ? `${seller.publicName || seller.name} - ${locationStr} konumunda 3D baskı hizmeti veren güvenilir satıcımız.`
      : `${seller.publicName || seller.name} - 3DEra'da 3D baskı hizmeti veren satıcımız.`,
    keywords: `${seller.publicName || seller.name}, 3d baskı, ${city}, 3d yazıcı, satıcı`,
    image,
    url,
    type: 'profile',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": seller.publicName || seller.name,
      "jobTitle": "3D Baskı Uzmanı",
      "url": url,
      "image": image,
      ...((city || country) && {
        address: {
          "@type": "PostalAddress",
          "addressLocality": city,
          "addressCountry": country
        }
      }),
      "worksFor": {
        "@type": "Organization",
        "name": "3DEra"
      }
    }
  };
};

export default useSEO;
